import { SyncAllPortalsButton } from '../components/SyncAllPortalsButton'
import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, type Page } from '../api/client'
import {
  Button, ButtonLink, Callout, Card, Checkbox, DataTable, Dialog, EmptyState, FilterBar, FormGrid,
  LoadingState, Pagination, Page as PageShell, PageHeader, Radio, SegmentedButton, Select, SortHeader,
  StatusPill, TextField, type PillTone,
  ExportMenu, IconButton,
} from '../components/ui'
import {
  CONTRACT_SORT_LABELS,
  CONTRACT_STATUS_COLORS,
  CONTRACT_STATUS_LABELS,
  PAYMENT_STATUS_OPTIONS,
  isContractSort,
  type PortalIssue,
} from '../api/contracts'
import type { Contract, ContractSort } from '../api/contracts'
import { batchesApi, type Batch } from '../api/batches'
import { fetchDepartments, fetchOperators, fetchProducts } from '../api/catalog'
import type { Department, Operator, Product } from '../api/catalog'
import {
  CONTRACT_COLUMNS, contractReferenceLabel, loadVisibleColumns, moveColumn, saveVisibleColumns,
  type ContractColumnKey,
} from '../lib/contractColumns'
import { SavedViewsBar } from '../components/SavedViewsBar'
import { PortalPhaseDot } from '../components/PortalPhaseDot'
import {
  ExportApprovalRequiredError, downloadExportFile, exportUrl, exportsApi, saveExportFile, withFormat,
} from '../api/exports'
import { useFeatureFlag } from '../lib/featureFlags'
import { ExportApprovalDialog } from '../components/ExportApprovalDialog'

// TASK-0025 / ADR-006: was a hardcoded 5-value subset (draft/approved/rejected/completed/
// cancelled — the legacy vocabulary) that no longer matches what the API returns now that
// Contract.Status is the validated 19-value ContractStatus enum. Both the badge lookup below and
// the filter dropdown's <option> list now use the shared, complete vocabulary from api/contracts.ts
// so this page and ContractDetailPage.tsx can't drift apart on labels/colors again.
const STATUS_COLORS = CONTRACT_STATUS_COLORS
const STATUS_LABELS = CONTRACT_STATUS_LABELS

/** Testo del tooltip sul badge di stato: problema del portale e, se c'è, la risoluzione segnata nel CRM. */
function portalIssueTitle(p: PortalIssue | null | undefined): string | null {
  if (!p || (!p.motivo && !p.statoPortale)) return null
  const quando = p.segnalatoIl ? ` (${new Date(p.segnalatoIl).toLocaleDateString('it-IT')})` : ''
  const righe = [`${p.portale}: ${p.statoPortale ?? ''}${quando}`.trim(), p.motivo ? `Motivo: ${p.motivo}` : null]
  if (p.risolta) righe.push(`Risolta il ${p.risoltaIl ? new Date(p.risoltaIl).toLocaleDateString('it-IT') : ''}: ${p.risoltaNota ?? ''} — in attesa del reparto`)
  return righe.filter(Boolean).join('\n')
}

// TASK-0084: was hardcoded on the dead not_collected/partial/full vocabulary that TASK-0064 already
// removed from the "Incasso" filter below — Contract.PaymentStatus really stores unpaid/partial/paid.
// Labels now come from PAYMENT_STATUS_OPTIONS (api/contracts.ts, introduced by TASK-0082) instead of
// being duplicated here, same principle as STATUS_COLORS/STATUS_LABELS above, so this page can't
// drift from ContractDetailPage.tsx's paymentStatusBadge again. PAYMENT_STATUS_OPTIONS carries no
// per-value color, so this local map mirrors the exact color scheme paymentStatusBadge already uses.
const COLL_COLORS: Record<string, string> = {
  unpaid: 'secondary',
  partial: 'warning',
  paid: 'success',
}
const COLL_LABELS: Record<string, string> = Object.fromEntries(
  PAYMENT_STATUS_OPTIONS.map(o => [o.value, o.label]),
)

const PAGE_SIZE = 25

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function fmtEur(n: number | undefined): string {
  if (n == null) return '—'
  return `€ ${n.toLocaleString('it-IT', { maximumFractionDigits: 0 })}`
}

// TASK-0204 (REQ-005-003): null-safe formatters for the two new forecast fields, same '—' fallback
// convention as fmtEur above — probability/expectedCloseDate are genuinely absent on every contract
// never given a forecast, not just on redacted responses.
function fmtProbability(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${n}%`
}

function fmtCloseDate(s: string | null | undefined): string {
  if (!s) return '—'
  return fmtDate(s)
}

/** Colore bootstrap dello stato (api/contracts.ts) -> tono della StatusPill. */
function pillTone(color: string | undefined): PillTone {
  switch (color) {
    case 'success': return 'success'
    case 'danger': return 'error'
    case 'warning': return 'warning'
    case 'info': return 'info'
    case 'primary': return 'primary'
    default: return 'neutral'
  }
}

export function ContractsPage() {
  // Applied filter state (triggers fetch)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [collectionStatus, setCollectionStatus] = useState('')
  // F-111 (REQ-006-009): settore, operatore e prodotto dalle liste del catalogo al posto del vecchio "Tipo" scritto nel codice.
  const [departmentId, setDepartmentId] = useState('')
  const [operatorId, setOperatorId] = useState('')
  // IMP-1: filtro per mandante (Numbers, Mastergroup, Seven...).
  const [mandanteId, setMandanteId] = useState('')
  const [pendingMandanteId, setPendingMandanteId] = useState('')
  const [mandanti, setMandanti] = useState<{ id: number; name: string }[]>([])
  const [productId, setProductId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  // F-094 (REQ-277-001): l'ordine lo decide il server; '' = quello di sempre (più recenti prima).
  const [sort, setSort] = useState<ContractSort | ''>('')

  // Pending form state
  const [pendingSearch, setPendingSearch] = useState('')
  const [pendingStatus, setPendingStatus] = useState('')
  const [pendingCollection, setPendingCollection] = useState('')
  const [pendingDepartmentId, setPendingDepartmentId] = useState('')
  const [pendingFilterOperatorId, setPendingFilterOperatorId] = useState('')
  const [pendingProductId, setPendingProductId] = useState('')
  const [departments, setDepartments] = useState<Department[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  const [visibleCols, setVisibleCols] = useState<ContractColumnKey[]>(() => loadVisibleColumns())
  const setCols = (next: ContractColumnKey[]) => {
    setVisibleCols(next)
    saveVisibleColumns(next)
  }
  // Una colonna riattivata va in fondo: l'ordine e' quello scelto dall'utente.
  const toggleCol = (k: ContractColumnKey) =>
    setCols(visibleCols.includes(k) ? visibleCols.filter(x => x !== k) : [...visibleCols, k])
  const [colsOpen, setColsOpen] = useState(false)
  const [products, setProducts] = useState<Product[]>([])
  const [pendingDateFrom, setPendingDateFrom] = useState('')
  const [pendingDateTo, setPendingDateTo] = useState('')

  const [data, setData] = useState<Page<Contract> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // TASK-0269 (F-015): the backoffice dashboard's tiles deep-link here as
  // /contracts?status=<ContractStatus>. Read-only, and deliberately minimal — seeds BOTH the
  // applied `status` and the pending "Stato" dropdown so the page opens already filtered and the
  // form shows what it is filtered by, without touching handleFilter/handleReset or adding any
  // filter capability beyond the single-value one the existing <select> already has (verified
  // before writing this: ContractsController.List's `status` query parameter accepts exactly one
  // ContractStatus, never a set — see the task report for what that means for tiles whose own
  // definition spans more than one status).
  //
  // P2-3 (review round, coordinator-verified): depends on `searchParams` (not `[]`) and
  // RESYNCS on every change, including back to '' when the query string carries no `status` at
  // all. Without this, arriving via /contracts?status=DaVerificare and then clicking "Contratti"
  // in the sidebar (same route, no query string) left the filter applied while the URL said
  // otherwise — a real component instance is reused across that navigation (same route, only the
  // search string changes), so a mount-only effect ran once and never again. handleFilter/
  // handleReset are untouched: they still set state directly without touching the URL, so this
  // effect only fires on an actual navigation with a different query string, never on a manual
  // filter submission.
  const [searchParams] = useSearchParams()
  useEffect(() => {
    const fromQuery = searchParams.get('status') ?? ''
    setStatus(fromQuery)
    setPendingStatus(fromQuery)
  }, [searchParams])

  // REQ-005-004: purely client-side view-mode toggle for the card below (Lista/
  // Calendario) -- same discipline as OperationsPage.tsx's `taskViewMode` (TASK-0226/0227) and
  // `apptViewMode` (TASK-0232): default 'list' so nobody who never touches the toggle sees any
  // behavior change, and it never touches `load()`: it only changes which JSX branch renders the
  // SAME already-fetched `items` array.
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')

  // TASK-0026 / ADR-007: bulk "Assegna a lotto" selection — only Verificata rows are selectable.
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [assigning, setAssigning] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null)

  // REQ-008-004: "pick an existing lotto" picker state — populated only when there is at least one
  // not-yet-submitted lotto for the resolved operator, so the common case (no candidates) never
  // shows a modal at all. See handleAssignToBatch/confirmBatchChoice.
  const [batchPickerOpen, setBatchPickerOpen] = useState(false)
  const [batchCandidates, setBatchCandidates] = useState<Batch[]>([])
  const [selectedBatchChoice, setSelectedBatchChoice] = useState<number | 'new'>('new')
  const [pendingOperatorId, setPendingOperatorId] = useState<number | null>(null)
  const [pendingContractIds, setPendingContractIds] = useState<number[]>([])

  // F-111: settori una volta sola; operatori e prodotti si restringono al settore/operatore scelti.
  useEffect(() => {
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
  }, [])
  useEffect(() => {
    const dep = pendingDepartmentId ? Number(pendingDepartmentId) : undefined
    fetchOperators(dep !== undefined ? { departmentId: dep } : undefined)
      .then(r => setOperators(r.items))
      .catch(() => setOperators([]))
  }, [pendingDepartmentId])
  useEffect(() => {
    api<{ items: { id: number; name: string }[] }>('/api/mandanti')
      .then(r => setMandanti(r.items))
      .catch(() => setMandanti([]))
  }, [])
  useEffect(() => {
    const dep = pendingDepartmentId ? Number(pendingDepartmentId) : undefined
    const op = pendingFilterOperatorId ? Number(pendingFilterOperatorId) : undefined
    fetchProducts({
      pageSize: 200,
      ...(dep !== undefined ? { departmentId: dep } : {}),
      ...(op !== undefined ? { operatorId: op } : {}),
    })
      .then(r => setProducts(r.items))
      .catch(() => setProducts([]))
  }, [pendingDepartmentId, pendingFilterOperatorId])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const q = new URLSearchParams()
      if (search) q.set('search', search)
      if (status) q.set('status', status)
      if (collectionStatus) q.set('paymentStatus', collectionStatus)
      if (departmentId) q.set('departmentId', departmentId)
      if (operatorId) q.set('operatorId', operatorId)
      if (mandanteId) q.set('mandanteId', mandanteId)
      if (productId) q.set('productId', productId)
      if (dateFrom) q.set('dateFrom', dateFrom)
      if (dateTo) q.set('dateTo', dateTo)
      if (sort) q.set('sort', sort)
      q.set('page', String(page))
      q.set('pageSize', String(PAGE_SIZE))
      const result = await api<Page<Contract>>(`/api/contracts?${q}`)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [search, status, collectionStatus, departmentId, operatorId, mandanteId, productId, dateFrom, dateTo, sort, page])

  useEffect(() => {
    void load()
  }, [load])

  function handleFilter(e?: React.FormEvent) {
    e?.preventDefault()
    setSearch(pendingSearch)
    setStatus(pendingStatus)
    setCollectionStatus(pendingCollection)
    setDepartmentId(pendingDepartmentId)
    setOperatorId(pendingFilterOperatorId)
    setMandanteId(pendingMandanteId)
    setProductId(pendingProductId)
    setDateFrom(pendingDateFrom)
    setDateTo(pendingDateTo)
    setPage(1)
  }

  // F-063 (REQ-242-001): viste salvate = i filtri APPLICATI adesso (non quelli ancora in digitazione).
  function getSavedViewState() {
    return { filters: { search, status, collectionStatus, departmentId, operatorId, mandanteId, productId, dateFrom, dateTo, sort } }
  }

  function applySavedViewState(state: { filters?: Record<string, unknown> }) {
    const f = state.filters ?? {}
    const str = (k: string) => (typeof f[k] === 'string' ? (f[k] as string) : '')
    setPendingSearch(str('search')); setSearch(str('search'))
    setPendingStatus(str('status')); setStatus(str('status'))
    setPendingCollection(str('collectionStatus')); setCollectionStatus(str('collectionStatus'))
    setPendingDepartmentId(str('departmentId')); setDepartmentId(str('departmentId'))
    setPendingFilterOperatorId(str('operatorId')); setOperatorId(str('operatorId'))
    setPendingMandanteId(str('mandanteId')); setMandanteId(str('mandanteId'))
    setPendingProductId(str('productId')); setProductId(str('productId'))
    setPendingDateFrom(str('dateFrom')); setDateFrom(str('dateFrom'))
    setPendingDateTo(str('dateTo')); setDateTo(str('dateTo'))
    setSort(isContractSort(f['sort']) ? f['sort'] : '')
    setPage(1)
  }

  function handleReset() {
    setPendingSearch('')
    setPendingStatus('')
    setPendingCollection('')
    setPendingDepartmentId('')
    setPendingFilterOperatorId('')
    setPendingProductId('')
    setPendingDateFrom('')
    setPendingDateTo('')
    setSearch('')
    setStatus('')
    setCollectionStatus('')
    setDepartmentId('')
    setOperatorId('')
    setProductId('')
    setDateFrom('')
    setDateTo('')
    setSort('')
    setPage(1)
  }

  /** F-094: clic su un'intestazione = ordina per quella colonna; un secondo clic inverte (o torna all'ordine di sempre). */
  function toggleSort(column: 'reference' | 'status' | 'created') {
    setSort(prev => {
      if (column === 'created') return prev === 'created_asc' ? 'created_desc' : prev === 'created_desc' || prev === '' ? 'created_asc' : 'created_desc'
      if (column === 'reference') return prev === 'reference_asc' ? '' : 'reference_asc'
      return prev === 'status' ? '' : 'status'
    })
    setPage(1)
  }

  const sortActiveKey = sort === 'reference_asc' ? 'reference' : sort === 'status' ? 'status'
    : sort === '' || sort === 'created_desc' || sort === 'created_asc' ? 'created' : null
  const sortDirection: 'asc' | 'desc' = sort === '' || sort === 'created_desc' ? 'desc' : 'asc'

  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /**
   * TASK-0026 / ADR-007: creates a new lotto for the selected Verificata contracts and assigns them
   * to it. Contract.OperatorId stays orphaned by design (ADR-007); the reliable path to a contract's
   * operator is Contract.ProductId -> Product.OperatorId. This page already has authenticated access
   * to the existing /api/products endpoint (which already returns operatorId), so it resolves each
   * selected contract's operator here as a convenience/UX hint and sends it explicitly to
   * /api/batches — but the backend does NOT trust this value: it independently re-resolves each
   * contract's real operator server-side (IProductOperatorReader — a BuildingBlocks interface
   * implemented by Catalog and consumed via DI, no ProjectReference/CatalogDbContext reference from
   * Contracts) and refuses the assignment if the supplied operatorId doesn't match. See
   * BatchesController.cs's remarks (independent review fix).
   *
   * REQ-008-004: the original cut (TASK-0026) always called batchesApi.create() first, so every
   * assignment made a brand-new lotto — there was no way to add practices to an already-assembling
   * one. This now looks for not-yet-submitted lotti (SubmittedAt == null) for the resolved operator
   * and, if any exist, opens a picker (confirmBatchChoice) offering them alongside "Nuovo lotto"
   * instead of assuming a new lotto is wanted. When there are no candidates the flow is unchanged —
   * create-and-assign happens immediately, no extra click for the common case.
   */
  async function handleAssignToBatch() {
    setAssignError(null)
    setAssignSuccess(null)
    const ids = Array.from(selected)
    if (ids.length === 0) return

    setAssigning(true)
    try {
      const products = await api<Page<{ id: number; operatorId?: number }>>('/api/products?pageSize=1000')
      const operatorByProduct = new Map(products.items.map(p => [p.id, p.operatorId]))

      const selectedContracts = items.filter(c => ids.includes(c.id))
      const operatorIds = new Set<number>()
      for (const c of selectedContracts) {
        const operatorId = c.productId != null ? operatorByProduct.get(c.productId) : undefined
        if (operatorId == null) {
          setAssignError(
            `La pratica ${c.referenceCode} non ha un operatore risolvibile dal prodotto associato.`,
          )
          setAssigning(false)
          return
        }
        operatorIds.add(operatorId)
      }
      if (operatorIds.size > 1) {
        setAssignError(
          'Le pratiche selezionate appartengono a operatori diversi: seleziona pratiche di un solo operatore.',
        )
        setAssigning(false)
        return
      }
      const operatorId = [...operatorIds][0]

      // REQ-008-004: same operator-match invariant BatchesController.AssignItems enforces server-side
      // (spec §8: a lotto groups practices for a single operator) — only offer lotti this assignment
      // could actually join.
      const candidates = await batchesApi.list({ status: 'InAttesaApprovazione', pageSize: 100 })
      const matching = candidates.items.filter(b => !b.submittedAt && b.operatorId === operatorId)

      if (matching.length === 0) {
        await createAndAssign(operatorId, ids)
        setAssigning(false)
        return
      }

      setBatchCandidates(matching)
      setPendingOperatorId(operatorId)
      setPendingContractIds(ids)
      setSelectedBatchChoice('new')
      setBatchPickerOpen(true)
      setAssigning(false)
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : "Errore durante l'assegnazione al lotto")
      setAssigning(false)
    }
  }

  /** REQ-008-004: the "Nuovo lotto" path — unchanged behaviour from the original single-step flow,
   * factored out so both the no-candidates fast path and the picker's "Nuovo lotto" choice share it.
   * Left to throw: callers decide how to report the error (and, for the picker, whether to keep it
   * open so the user can retry). */
  async function createAndAssign(operatorId: number, ids: number[]) {
    const batch = await batchesApi.create(operatorId)
    await batchesApi.assignItems(batch.id, ids, operatorId)
    setAssignSuccess(`Lotto ${batch.batchCode} creato con ${ids.length} pratiche assegnate.`)
    setSelected(new Set())
    await load()
  }

  /** REQ-008-004: confirms the picker's choice — either creates a new lotto (unchanged path) or
   * assigns straight into the chosen existing not-yet-submitted lotto. Closes the modal only once the
   * call actually succeeds; on failure the error is shown inside the still-open modal so the user can
   * pick again or retry, same as ApprovalsPage.tsx's reject modal. */
  async function confirmBatchChoice() {
    if (pendingOperatorId == null || pendingContractIds.length === 0) return
    setAssigning(true)
    setAssignError(null)
    try {
      if (selectedBatchChoice === 'new') {
        await createAndAssign(pendingOperatorId, pendingContractIds)
      } else {
        await batchesApi.assignItems(selectedBatchChoice, pendingContractIds, pendingOperatorId)
        const chosen = batchCandidates.find(b => b.id === selectedBatchChoice)
        setAssignSuccess(
          `${pendingContractIds.length} pratiche assegnate al lotto ${chosen?.batchCode ?? selectedBatchChoice}.`,
        )
        setSelected(new Set())
        await load()
      }
      setBatchPickerOpen(false)
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : "Errore durante l'assegnazione al lotto")
    } finally {
      setAssigning(false)
    }
  }

  // F-117 (REQ-266-001): export CSV con i filtri applicati. L'export del server conosce ricerca, stato, incasso,
  // settore e date; operatore e prodotto non gli appartengono, quindi con quei filtri il pulsante resta spento.
  const [exporting, setExporting] = useState(false)
  const excelOn = useFeatureFlag('export.excel')
  const [exportError, setExportError] = useState<string | null>(null)
  const [approvalFilters, setApprovalFilters] = useState<Record<string, string> | null>(null)
  const exportFilters: Record<string, string> = {
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(collectionStatus ? { paymentStatus: collectionStatus } : {}),
    ...(departmentId ? { departmentId } : {}),
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  }

  async function handleExport(format: 'csv' | 'xlsx' | 'pdf' = 'csv') {
    setExporting(true)
    setExportError(null)
    try {
      if (format !== 'csv') saveExportFile(await downloadExportFile(withFormat(exportUrl('contracts', exportFilters), format)))
      else await exportsApi.downloadContracts(exportFilters)
    } catch (e) {
      if (e instanceof ExportApprovalRequiredError) setApprovalFilters(exportFilters)
      else setExportError(e instanceof Error ? e.message : "Errore durante l'export")
    } finally {
      setExporting(false)
    }
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0

  const activeFilterCount = [search, status, collectionStatus, departmentId, operatorId, mandanteId, productId, dateFrom, dateTo]
    .filter(v => v !== '').length

  const NUM_COLS = new Set<ContractColumnKey>(['amount', 'expected', 'accrued', 'probability'])
  const SORTABLE: Partial<Record<ContractColumnKey, 'reference' | 'status' | 'created'>> = { reference: 'reference', status: 'status', date: 'created' }
  const headerOf = (k: ContractColumnKey) => {
    const label = CONTRACT_COLUMNS.find(c => c.key === k)!.label
    const sortKey = SORTABLE[k]
    return sortKey
      ? <SortHeader key={k} label={label} sortKey={sortKey} activeKey={sortActiveKey} direction={sortDirection} onSort={() => toggleSort(sortKey)} />
      : <th key={k}>{label}</th>
  }
  const cellOf = (c: Contract, k: ContractColumnKey) => {
    switch (k) {
      case 'reference':
        return (
          <a
            href={`/contracts/${c.id}`}
            className="md-typescale-emphasized-body-medium text-decoration-none"
            title={c.referenceCode || undefined}
          >
            {contractReferenceLabel({
              id: c.id,
              referenceCode: c.referenceCode,
              mandante: mandanti.find(m => m.id === c.mandanteId)?.name,
              operator: operators.find(o => o.id === c.operatorId)?.name,
              date: fmtDate(c.signedAt ?? c.createdAt),
            })}
          </a>
        )
      case 'customer': return c.customerName ?? '—'
      case 'operator': return operators.find(o => o.id === c.operatorId)?.name ?? '—'
      case 'status': {
        // Pratica sospesa/KO: passando sul badge si legge subito il problema segnalato dal portale.
        const tip = portalIssueTitle(c.portalIssue)
        // Card sul portale in una fase operativa: il «!» accanto dà la fase vera e gli ultimi messaggi della chat.
        return (
          <span className="d-inline-flex align-items-center">
            <StatusPill tone={pillTone(STATUS_COLORS[c.status])} title={tip ?? undefined} style={tip ? { cursor: 'help' } : undefined}>
              {STATUS_LABELS[c.status] ?? c.status}{tip ? (c.portalIssue?.risolta ? ' ✓' : ' !') : ''}
            </StatusPill>
            <PortalPhaseDot phase={c.portalPhase} />
          </span>
        )
      }
      case 'collection':
        return <StatusPill tone={pillTone(COLL_COLORS[c.paymentStatus])}>{COLL_LABELS[c.paymentStatus] ?? c.paymentStatus}</StatusPill>
      case 'amount': return fmtEur(c.expectedAmount)
      case 'expected': return fmtEur(c.expectedRevenue ?? undefined)
      case 'accrued': return fmtEur(c.accruedRevenue ?? undefined)
      case 'productType': return c.contractType ?? '—'
      case 'paymentMethod': return '—'
      case 'probability': return fmtProbability(c.probability)
      case 'closeDate': return fmtCloseDate(c.expectedCloseDate)
      case 'date': return fmtDate(c.signedAt ?? c.createdAt)
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Contratti"
        icon="description"
        count={total}
        actions={
          <>
            {/* Aggiorna tutta la produzione dai portali (Ermes, Teamwork, Coraly) senza aspettare i giri ogni 10 minuti. */}
            <SyncAllPortalsButton onDone={() => { void load() }} />
            {selected.size > 0 && (
              <Button
                variant="outlined"
                disabled={assigning}
                onClick={() => void handleAssignToBatch()}
              >
                {assigning ? 'Assegnazione…' : `Assegna a lotto (${selected.size})`}
              </Button>
            )}
            <ExportMenu
              busy={exporting}
              disabled={!!operatorId || !!productId}
              formats={excelOn ? ['csv', 'xlsx', 'pdf'] : ['csv']}
              title={operatorId || productId ? 'L’export non filtra per operatore o prodotto: toglili per esportare.' : 'Esporta le pratiche con i filtri applicati'}
              onExport={f => void handleExport(f)}
            />
            <ButtonLink to="/contracts/create" variant="filled" icon="add">
              Nuovo contratto
            </ButtonLink>
          </>
        }
      />

      {exportError && <Callout tone="error">{exportError}</Callout>}
      {approvalFilters && (
        <ExportApprovalDialog
          dataset="contracts"
          datasetLabel="Pratiche"
          filters={approvalFilters}
          onClose={() => setApprovalFilters(null)}
        />
      )}

      <SavedViewsBar pageKey="contracts" getState={getSavedViewState} applyState={applySavedViewState} />

      {/* Sempre in vista solo ricerca e stato; il resto (e l'ordinamento, F-094) sta in "Altri filtri". */}
      <FilterBar onSubmit={() => handleFilter()} onReset={handleReset} activeCount={activeFilterCount}
        moreActive={[pendingCollection, pendingDateFrom, pendingDateTo, pendingDepartmentId, pendingFilterOperatorId, pendingMandanteId, pendingProductId, sort].filter(Boolean).length}
        more={<>
        <Select
          id="contracts-sort"
          label="Ordina per"
          value={sort || 'created_desc'}
          onChange={e => {
            setSort(isContractSort(e.target.value) && e.target.value !== 'created_desc' ? e.target.value : '')
            setPage(1)
          }}
        >
          {(Object.keys(CONTRACT_SORT_LABELS) as ContractSort[]).map(k => (
            <option key={k} value={k}>{CONTRACT_SORT_LABELS[k]}</option>
          ))}
        </Select>
        <Select label="Incasso" value={pendingCollection} onChange={e => setPendingCollection(e.target.value)}>
          <option value="">Tutti</option>
          {/* TASK-0064: values reconciled to match Contract.PaymentStatus's own
              unpaid|partial|paid domain vocabulary — used to send
              not_collected/partial/full, of which only "partial" ever matched anything on
              the backend. Purely an internal identifier, so the Italian labels stay as-is. */}
          <option value="unpaid">No</option>
          <option value="partial">Parziale</option>
          <option value="paid">Incassato</option>
        </Select>
        <TextField label="Dal" type="date" value={pendingDateFrom} onChange={e => setPendingDateFrom(e.target.value)} />
        <TextField label="Al" type="date" value={pendingDateTo} onChange={e => setPendingDateTo(e.target.value)} />
        <Select
          label="Settore"
          value={pendingDepartmentId}
          onChange={e => {
            setPendingDepartmentId(e.target.value)
            setPendingFilterOperatorId('')
            setPendingProductId('')
          }}
        >
          <option value="">Tutti</option>
          {departments.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </Select>
        <Select
          label="Operatore"
          value={pendingFilterOperatorId}
          onChange={e => {
            setPendingFilterOperatorId(e.target.value)
            setPendingProductId('')
          }}
        >
          <option value="">Tutti</option>
          {operators.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </Select>
        <Select label="Mandante" value={pendingMandanteId} onChange={e => setPendingMandanteId(e.target.value)}>
          <option value="">Tutti</option>
          {mandanti.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </Select>
        <Select label="Prodotto" value={pendingProductId} onChange={e => setPendingProductId(e.target.value)}>
          <option value="">Tutti</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </Select>
        </>}>
        <TextField
          label="Cerca per nome, POD, telefono..."
          type="text"
          value={pendingSearch}
          onChange={e => setPendingSearch(e.target.value)}
        />
        <Select label="Stato" value={pendingStatus} onChange={e => setPendingStatus(e.target.value)}>
          <option value="">Tutti</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}
      {assignError && !batchPickerOpen && <Callout tone="error">{assignError}</Callout>}
      {assignSuccess && <Callout tone="success">{assignSuccess}</Callout>}

      {/* REQ-008-004: "pick an existing lotto" picker — only ever opened when handleAssignToBatch
          found at least one not-yet-submitted lotto for the resolved operator. Same modal shape as
          ApprovalsPage.tsx's reject dialog: actionError shown inside, modal stays open on failure. */}
      <Dialog
        open={batchPickerOpen}
        onClose={() => setBatchPickerOpen(false)}
        title="Assegna a lotto"
        supportingText="Esistono già lotti non ancora inviati per questo operatore: scegli se aggiungere le pratiche selezionate a uno di questi oppure crearne uno nuovo."
        as="form"
        onSubmit={e => { e.preventDefault(); void confirmBatchChoice() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={() => setBatchPickerOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              disabled={assigning}
            >
              {assigning ? 'Assegnazione…' : 'Conferma'}
            </Button>
          </>
        }
      >
        {assignError && <Callout tone="error">{assignError}</Callout>}
        <FormGrid columns={1}>
          <Radio
            name="batchChoice"
            id="batch-choice-new"
            checked={selectedBatchChoice === 'new'}
            onChange={() => setSelectedBatchChoice('new')}
            label="Nuovo lotto"
          />
          {batchCandidates.map(b => (
            <Radio
              key={b.id}
              name="batchChoice"
              id={`batch-choice-${b.id}`}
              checked={selectedBatchChoice === b.id}
              onChange={() => setSelectedBatchChoice(b.id)}
              label={b.batchCode}
            />
          ))}
        </FormGrid>
      </Dialog>

      {/* REQ-005-004: Lista/Calendario view toggle -- purely client-side, same segmented-toggle pattern/
          onClick pattern as OperationsPage.tsx's `taskViewMode`/`apptViewMode` toggles
          (TASK-0226/0227/0232). It touches no filter state and does not trigger `load()`. */}
      <div className="d-flex justify-content-end align-items-center gap-2" style={{ position: 'relative' }}>
        {viewMode === 'list' && (
          <Button variant="outlined" size="sm" icon="view_column" onClick={() => setColsOpen(o => !o)} aria-expanded={colsOpen}>
            Colonne
          </Button>
        )}
        {viewMode === 'list' && colsOpen && (
          <div
            role="dialog"
            aria-label="Colonne"
            className="md-surface-container"
            style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, marginTop: 4, padding: 8, minWidth: 260,
              background: 'var(--md-sys-color-surface-container, #fff)', borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,.18)' }}
          >
            {[...visibleCols, ...CONTRACT_COLUMNS.map(c => c.key).filter(k => !visibleCols.includes(k))].map(key => {
              const col = CONTRACT_COLUMNS.find(c => c.key === key)!
              const on = visibleCols.includes(key)
              const i = visibleCols.indexOf(key)
              return (
                <div key={key} className="d-flex align-items-center justify-content-between gap-2">
                  <Checkbox label={col.label} checked={on} onChange={() => toggleCol(key)} />
                  {on && (
                    <span className="d-flex">
                      <IconButton icon="arrow_upward" size="sm" aria-label={`Sposta ${col.label} prima`} disabled={i === 0} onClick={() => setCols(moveColumn(visibleCols, key, -1))} />
                      <IconButton icon="arrow_downward" size="sm" aria-label={`Sposta ${col.label} dopo`} disabled={i === visibleCols.length - 1} onClick={() => setCols(moveColumn(visibleCols, key, 1))} />
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )}
        <SegmentedButton
          aria-label="Modalità di visualizzazione"
          options={[
            { value: 'list', label: 'Lista' },
            { value: 'calendar', label: 'Calendario' },
          ]}
          value={viewMode}
          onChange={v => setViewMode(v === 'calendar' ? 'calendar' : 'list')}
        />
      </div>

      {/* Table card */}
      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="description" title="Nessun contratto trovato" />
        ) : (
          <>
            {viewMode === 'calendar' ? (
              // REQ-005-004: Calendar view -- read-only monthly grid for the current month/year
              // only (`new Date()` at render time, no new state -- deliberately no month-forward/
              // back navigation, exactly the same restriction as OperationsPage.tsx's Task
              // (TASK-0227) and Appointment (TASK-0232) calendars). Same `items` array as the
              // table below, i.e. the current page of the current filter, grouped client-side by
              // `expectedCloseDate` -- the "Chiusura prevista" field REQ-005-003 already added
              // (TASK-0204). No API change, no extra fetch.
              //
              // Contracts whose `expectedCloseDate` is null/absent (no forecast ever recorded) are
              // simply not placed in any cell: there is no sensible fallback date and inventing one
              // (createdAt) would misreport a forecast. They stay fully visible in the list view,
              // which is the default.
              (() => {
                const today = new Date()
                const year = today.getFullYear()
                const month = today.getMonth()
                const daysInMonth = new Date(year, month + 1, 0).getDate()
                // getDay(): 0=Sunday..6=Saturday -- shifted to a Monday-first week (it-IT
                // convention), same as the two calendars in OperationsPage.tsx.
                const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7
                const cells: Array<number | null> = [
                  ...Array.from({ length: firstWeekday }, () => null),
                  ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
                ]
                while (cells.length % 7 !== 0) cells.push(null)
                const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
                // `expectedCloseDate` is a bare `YYYY-MM-DD` (server-side `DateOnly?`, see the
                // Contract type in api/contracts.ts), NOT an instant: parsing it with `new Date()`
                // would read it as UTC midnight and could shift the day by one in a negative-offset
                // timezone. The two OperationsPage calendars can use `new Date(...)` because they
                // bucket a real timestamp (`startAt`/`dueDate`); here the calendar-date parts are
                // compared literally instead.
                const closesOn = (c: Contract, day: number): boolean => {
                  if (!c.expectedCloseDate) return false
                  const [y, m, d] = c.expectedCloseDate.slice(0, 10).split('-').map(Number)
                  return y === year && m === month + 1 && d === day
                }
                return (
                  <div className="p-3">
                    <div className="md-typescale-emphasized-body-medium mb-2">
                      {today.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                    </div>
                    <div className="md-u-scroll-x">
                    <div className="d-grid md-u-grid-7">
                      {weekdayLabels.map(label => (
                        <div key={label} className="md-typescale-emphasized-body-small text-center text-muted">
                          {label}
                        </div>
                      ))}
                      {cells.map((day, idx) => {
                        if (day === null) {
                          return <div key={`empty-${idx}`} className="md-u-minh-88" />
                        }
                        const dayContracts = items.filter(c => closesOn(c, day))
                        return (
                          <Card as="div" variant="outlined" padding="sm" key={day} className="gap-1 md-u-minh-88">
                            <div className="md-typescale-body-small text-muted mb-1">{day}</div>
                            <div className="d-flex flex-column gap-1">
                              {dayContracts.map(c => (
                                <a
                                  key={c.id}
                                  href={`/contracts/${c.id}`}
                                  title={`${c.referenceCode || `#${c.id}`} — Cliente ${c.customerName ?? '—'}`}
                                  className="text-decoration-none d-block"
                                >
                                  <StatusPill tone={pillTone(STATUS_COLORS[c.status])} className="w-100 text-truncate">
                                    {c.referenceCode || `#${c.id}`}
                                    <span className="d-block text-truncate">
                                      Cliente {c.customerName ?? '—'}
                                    </span>
                                  </StatusPill>
                                </a>
                              ))}
                            </div>
                          </Card>
                        )
                      })}
                    </div>
                    </div>
                  </div>
                )
              })()
            ) : (
              <>
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th></th>
                    {visibleCols.map(headerOf)}
                  </tr>
                </thead>
                <tbody>
                  {items.map(c => (
                    <tr key={c.id}>
                      <td>
                        {c.status === 'Verificata' && (
                          <Checkbox
                            label={<span className="md-sr-only">{`Seleziona ${c.referenceCode || c.id}`}</span>}
                            checked={selected.has(c.id)}
                            onChange={() => toggleSelect(c.id)}
                          />
                        )}
                      </td>
                      {visibleCols.map(k => <td key={k} className={NUM_COLS.has(k) ? 'md-num' : undefined}>{cellOf(c, k)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              </>
            )}
            {/* REQ-005-004: the pager stays shared by both views -- the calendar renders the
                SAME fetched page, so the pager is how the user reaches the other pages' rows. */}
            <div className="d-flex justify-content-center p-2">
              <Pagination
                page={page}
                total={total}
                pageSize={PAGE_SIZE}
                onChange={setPage}
              />
            </div>
          </>
        )}
      </Card>
    </PageShell>
  )
}
